import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { Typography, Pagination, Form } from "antd";
import { AnchorLinkItemProps } from "antd/es/anchor/Anchor";

import { AseguradoPoliza, Car, Endorsement, InputFactoryProp, InsurancePolicy, LifePossession, Possessions, PossessionsType, Ubication, Vessel } from "../../../../../shared/interfaces";
import { Anchor, ColSize, ErrorResults, InputFactory, RowSize } from './../../../../../shared/components';
import { COLORS } from "../../../../../config/themes/colors";
import { CollapsablePossession } from "../CollapsablePossession";
import { PolicyNotFound } from "../../../InsurancePolicyList/components";

import './index.css';

interface PossessionsTabProps {
    insurancePolicyDetail: InsurancePolicy;
    possessionsList?: Array<Possessions>;
    endorsement?: Endorsement;
    showError?: boolean;
    insuredsByPolicy?: AseguradoPoliza;
}

const MOCK_SECTIONS: Array<AnchorLinkItemProps> = [
    {
        key: 'listado-de-bienes-asegurados',
        href: '#listado-de-bienes-asegurados',
        title: 'Listado de Bienes Asegurados'
    }
];

export const PossessionsTab: FC<PossessionsTabProps> = ({ insurancePolicyDetail, possessionsList = [], showError = false, endorsement, insuredsByPolicy }) => {

    const [ possessions, setPossessions ] = useState( possessionsList );
    const [ isSearching, setIsSearching ] = useState( false );

    const [ filterForm ] = Form.useForm();

    const onChangePage = useCallback((page: number, pageSize: number) => {
        const indexOfLastPoliza = page * pageSize;
        const indexOfFirstPoliza = indexOfLastPoliza - pageSize;
        const currentPolizas = possessionsList?.slice(indexOfFirstPoliza, indexOfLastPoliza);
        setPossessions(currentPolizas || []);
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        })
    }, [ possessionsList ]);

    const initialStateFilters = useMemo(() => {
        if( insurancePolicyDetail?.possessionsType === PossessionsType.BIENES_VIDA ) {
            return { fullName: '', documentNumber: '' }
        }

        if( insurancePolicyDetail?.possessionsType === PossessionsType.BIENES_AUTOS || insurancePolicyDetail?.possessionsType === PossessionsType.BIENES_EMBARCACIONES ) {
            return { patent: '' };
        };

        if( insurancePolicyDetail?.possessionsType === PossessionsType.BIENES_RIESGOS_VARIOS ) {
            return { address: '' };
        }

        return {}
    }, [ insurancePolicyDetail ]);

    const filterPossessions = useCallback(() => {

        let filteredPossesions: Array<Possessions> = [];

        if( insurancePolicyDetail?.possessionsType === PossessionsType.BIENES_VIDA ) {
            filteredPossesions = possessionsList.filter((possesion: Possessions) => 
                (filterForm.getFieldValue('fullName') === undefined || (possesion as LifePossession).integrVida.toLowerCase().includes(filterForm.getFieldValue('fullName').toLowerCase())) &&
                (filterForm.getFieldValue('document') === undefined || (possesion as LifePossession).nroDocVida.includes(filterForm.getFieldValue('document')))
            );
        }

        if( insurancePolicyDetail?.possessionsType === PossessionsType.BIENES_AUTOS ) {
            filteredPossesions = possessionsList.filter((possesion: Possessions) => 
                (filterForm.getFieldValue('patent') === undefined || (possesion as Car).patente.toLowerCase().includes(filterForm.getFieldValue('patent').toLowerCase()))
            );
        }

        if( insurancePolicyDetail?.possessionsType === PossessionsType.BIENES_EMBARCACIONES ) {
            filteredPossesions = possessionsList.filter((possesion: Possessions) => 
                (filterForm.getFieldValue('patent') === undefined || (possesion as Vessel).matricula.toLowerCase().includes(filterForm.getFieldValue('patent').toLowerCase()))
            );
        }

        if( insurancePolicyDetail?.possessionsType === PossessionsType.BIENES_RIESGOS_VARIOS ) {
            filteredPossesions = possessionsList.filter((possesion: Possessions) => 
                (filterForm.getFieldValue('address') === undefined || (possesion as Ubication).direccion.toLowerCase().includes(filterForm.getFieldValue('address').toLowerCase()))
            );
        }

        return filteredPossesions;
    }, [ insurancePolicyDetail, filterForm, possessionsList ])

    const onPressSearch = () => {
        setIsSearching(true);

        const filteredPossessions = filterPossessions();

        setPossessions( filteredPossessions );

        setTimeout(() => setIsSearching(false), 2000)
    };
    
    const inputsFilter: Array<InputFactoryProp> =  useMemo(() => {
        let inputs: Array<InputFactoryProp> = [];

        if( insurancePolicyDetail?.possessionsType === PossessionsType.BIENES_VIDA ) {
            inputs.push({ element: 'input', type: 'text', placeHolder: 'Apellido y Nombre', name: 'fullName' })
            inputs.push({ element: 'input', type: 'text', placeHolder: 'Documento', name: 'document' })
        }

        if( insurancePolicyDetail?.possessionsType === PossessionsType.BIENES_AUTOS ) {
            inputs.push({ element: 'input', type: 'text', placeHolder: 'Patente', name: 'patent' });
        }

        if( insurancePolicyDetail?.possessionsType === PossessionsType.BIENES_EMBARCACIONES ) {
            inputs.push({ element: 'input', type: 'text', placeHolder: 'Matrícula', name: 'patent' });
        }

        if( insurancePolicyDetail?.possessionsType === PossessionsType.BIENES_RIESGOS_VARIOS ) {
            inputs.push({ element: 'input', type: 'text', placeHolder: 'Dirección', name: 'address' });
        }

        return inputs;
    }, [ insurancePolicyDetail ]);

    useEffect(() => {
        onChangePage(1, 10)
        /* eslint-disable-next-line */
    }, [])

    return (
        <div className="possessions-tab-container">
            <RowSize rowProps={{gutter:[0 , 0]}} >
                {
                    showError
                        ? <ErrorResults title="Hubo un error intentando obtener el listado de bienes" />
                        : (
                            <>             
                                <ColSize size={ 85 } className="col-possessions-item" colProps={{ xs:{span: 21}, sm:{span: 21}}}>
                                    
                                    <Typography.Title level={ 4 } style={{ margin: 0, marginBottom: 10, marginTop: 20, color: COLORS.principal[100] }}>
                                        Listado de Bienes Asegurados
                                    </Typography.Title>

                                    <RowSize rowProps={{align:"middle", justify:"space-between", gutter:[0 , 0]}} className="row-possessions-input" >
                                        <InputFactory 
                                            form={ filterForm }
                                            items={ inputsFilter }
                                            onFilter={ onPressSearch }
                                            isLoading={ false }
                                            initialState={ initialStateFilters }
                                            columnSizes={{ xl: { span: 12 } }}
                                        />
                                    </RowSize>

                                    {/* LISTADO DE DROPDOWNS */}            
                                    <RowSize rowProps={{gutter:[0 , 0]}} className="row-possessions-dropdowns">
                                        <ColSize size={100}>
                                            {
                                                possessions.length 
                                                    ?   possessions.map(
                                                            ( possession, i ) => (
                                                                <CollapsablePossession 
                                                                    key={ i } 
                                                                    isLoading={ isSearching } 
                                                                    type={ insurancePolicyDetail?.possessionsType! }    
                                                                    possessionData={ possession }
                                                                    insurancePolicyDetail={ insurancePolicyDetail }
                                                                    endorsement={endorsement}
                                                                    insuredsByPolicy={insuredsByPolicy}
                                                                    possessions={possessions}
                                                                    
                                                                />
                                                            )
                                                        )
                                                    : <PolicyNotFound />
                                            }
                                        </ColSize>
                                    </RowSize>
                                            
                                    

                                    {/* PAGINADO */}
                                    {
                                        possessions?.length > 0 && (
                                            <RowSize rowProps={{justify:"end", gutter:[0 , 0]}} className="row-possessions-paginate" >
                                                <Pagination
                                                    total={ possessions.length }
                                                    defaultPageSize={ 10 }
                                                    defaultCurrent={ 1 }
                                                    onChange={ onChangePage }
                                                />
                                            </RowSize>
                                        )
                                    }
                                </ColSize>

                                <ColSize size={ 9 } colProps={{id:"possessions-tab-anchor-container", xs:{span: 3}, sm:{span: 3}}} >
                                    <Anchor 
                                        items={ MOCK_SECTIONS }
                                        containerId="possessions-tab-anchor-container"
                                    />
                                </ColSize>
                            </>
                        )
                }
            </RowSize>
        </div>
    )
}
